import { z } from 'zod';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance } from '../main';
import { apiRequest, loginRequest } from '../config/authConfig';
import { validateApiResponse, taxReturnSchema, taxDocumentSchema, dashboardStatsSchema } from '@/schemas';
import { DEFAULT_API_TIMEOUT_MS, DEFAULT_SEARCH_MAX_RESULTS } from '@/constants';
import type {
  StaffNotification,
  StaffNotificationFeed,
  TaxReturn,
  TaxDocument,
  DashboardStats,
  OutstandingItems,
  ApiResponse,
  TaxReturnFilters,
  SystemUser,
  DocumentUploadLink,
  AppointmentAttendeesResponse,
  ResolveAttendeesRequest,
  AddReturnToAppointmentRequest,
  SearchResults,
  ClientBookkeepingStatus,
  DrakeChecklist,
  DrakeMatchStatus,
  ContactInfo,
  AccountInfo,
  RecordCorrectionRequest,
  DrakeExtractionAnalytics,
  CorrectionPattern,
  DrakePromptTemplate,
  PasswordProtectedPdfError,
  RetryWithPasswordRequest,
  PreparerBill,
  GeneratePreparerBillRequest,
  RecordPaymentRequest,
  XeroBillResult,
  PreparerUnbilledSummary,
  BulkStatusUpdateResult,
  StaffRole,
  XeroContact,
  TaxClientListItem,
  ExtensionInfo,
  FilingImportSummary,
  MatchReviewPage,
  PriorityListResponse,
  BootstrapPassResponse,
  ContactSearchResult,
  ContactSearchKind,
} from '@/types';

// Re-export types for consumers
export type {
  DrakeChecklist,
  DrakeMatchStatus,
  ContactInfo,
  RecordCorrectionRequest,
  DrakeExtractionAnalytics,
  CorrectionPattern,
  DrakePromptTemplate,
};

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

// Get access token for API calls
async function getAccessToken(): Promise<string | null> {
  const account = msalInstance.getActiveAccount();
  if (!account) {
    return null;
  }

  try {
    // Use apiRequest scopes to get token with correct audience for our API
    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest,
      account,
    });
    return response.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      // Token expired or needs interaction, trigger login
      await msalInstance.acquireTokenRedirect(loginRequest);
      return null;
    }
    console.error('Failed to acquire token:', error);
    return null;
  }
}

/** Append appointmentId as a query param for Cosmos point-read optimization */
function withAppointmentId(url: string, appointmentId?: string): string {
  if (!appointmentId) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}appointmentId=${encodeURIComponent(appointmentId)}`;
}

async function fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DEFAULT_API_TIMEOUT_MS);

  // Merge internal timeout signal with any caller-provided signal so both can abort
  const signals = [controller.signal, options?.signal].filter(Boolean) as AbortSignal[];
  const mergedSignal = signals.length > 1 ? AbortSignal.any(signals) : signals[0];

  try {
    const token = await getAccessToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge with any additional headers from options
    if (options?.headers) {
      const optHeaders = options.headers as Record<string, string>;
      Object.assign(headers, optHeaders);
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
      signal: mergedSignal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `API Error: ${response.status}`);
    }

    const body = await response.json();
    // Some endpoints CreateResponse(non-200) then WriteAsJsonAsync, which resets
    // the wire status to 200 in the isolated-worker runtime — surfacing server
    // errors as success-shaped envelopes. Treat {success:false} as a hard error
    // so callers don't silently render `r.data ?? []`.
    if (body && typeof body === 'object' && body.success === false) {
      throw new Error(body.error || 'Request failed');
    }
    return body;
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// Tax Returns API

export async function getTaxReturns(filters?: TaxReturnFilters): Promise<TaxReturn[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.preparerId) params.append('preparerId', filters.preparerId);
  if (filters?.taxYear) params.append('taxYear', filters.taxYear.toString());
  if (filters?.appointmentType) params.append('appointmentType', filters.appointmentType);
  if (filters?.scheduledRange) params.append('scheduledRange', filters.scheduledRange);
  if (filters?.clientId) params.append('clientId', filters.clientId);
  if (filters?.appointmentId) params.append('appointmentId', filters.appointmentId);
  if (filters?.unassigned) params.append('unassigned', 'true');

  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<TaxReturn[]>>(`/tax-returns${query}`);
  return validateApiResponse(response.data || [], z.array(taxReturnSchema), 'getTaxReturns');
}

export async function getTaxReturn(id: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${id}`);
  if (!response.data) throw new Error('Tax return not found');
  return validateApiResponse(response.data, taxReturnSchema, 'getTaxReturn');
}

export async function createTaxReturn(data: {
  appointmentId: string;
  taxYear?: number;
  returnType?: string;
  preparerId?: string;
  preparerName?: string;
}): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>('/tax-returns', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.data) throw new Error('Failed to create tax return');
  return response.data;
}

export async function createDropoffTaxReturn(data: {
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  taxYear?: number;
  returnType?: string;
  preparerId?: string;
  preparerName?: string;
  contactId?: string;
  accountId?: string;
  isAmendment?: boolean;
}): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>('/tax-returns/dropoff', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.data) throw new Error('Failed to create dropoff tax return');
  return response.data;
}

export async function updateTaxReturn(id: string, updates: Partial<TaxReturn>): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  if (!response.data) throw new Error('Failed to update tax return');
  return response.data;
}

export async function updateTaxReturnStatus(
  id: string,
  status: string,
  options?: { changedBy?: string; changedByName?: string; notes?: string }
): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${id}/status`, {
    method: 'POST',
    body: JSON.stringify({ status, ...options }),
  });
  if (!response.data) throw new Error('Failed to update status');
  return response.data;
}

export async function bulkUpdateTaxReturnStatus(
  taxReturnIds: string[],
  options: { status?: string; efileStatus?: string; changedBy?: string; changedByName?: string; notes?: string }
): Promise<BulkStatusUpdateResult> {
  const response = await fetchApi<ApiResponse<BulkStatusUpdateResult>>(`/tax-returns/bulk-status-update`, {
    method: 'POST',
    body: JSON.stringify({ taxReturnIds, ...options }),
  });
  if (!response.data) throw new Error('Failed to bulk update status');
  return response.data;
}

export async function setTaxReturnExtension(
  id: string,
  body: ExtensionInfo
): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${id}/extension`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (!response.data) throw new Error('Failed to set extension');
  return response.data;
}

export async function clearTaxReturnExtension(id: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${id}/extension`, {
    method: 'DELETE',
  });
  if (!response.data) throw new Error('Failed to clear extension');
  return response.data;
}

export async function deleteTaxReturn(id: string): Promise<void> {
  await fetchApi<ApiResponse<void>>(`/tax-returns/${id}`, {
    method: 'DELETE',
  });
}

export async function mergeTaxReturns(targetId: string, sourceId: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${targetId}/merge/${sourceId}`, {
    method: 'POST',
  });
  if (!response.data) throw new Error('Failed to merge tax returns');
  return response.data;
}

export async function mergeSpouseIntoReturn(targetId: string, sourceReturnId: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${targetId}/merge-spouse`, {
    method: 'POST',
    body: JSON.stringify({ sourceReturnId }),
  });
  if (!response.data) throw new Error('Failed to merge spouse into return');
  return response.data;
}

export async function mergeScheduleCIntoReturn(targetId: string, sourceReturnId: string, useBillingName: boolean): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${targetId}/merge-schedule-c`, {
    method: 'POST',
    body: JSON.stringify({ sourceReturnId, useBillingName }),
  });
  if (!response.data) throw new Error('Failed to merge Schedule C into return');
  return response.data;
}

export async function swapTaxpayerAndSpouse(id: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/tax-returns/${id}/swap-taxpayer-spouse`, {
    method: 'POST',
  });
  if (!response.data) throw new Error('Failed to swap taxpayer and spouse');
  return response.data;
}

// Xero Invoice Generation API

export interface GenerateInvoiceRequest {
  totalAmount: number;
  reference?: string;
}

export interface GenerateInvoiceResponse {
  success: boolean;
  invoiceId: string;
  invoiceNumber: string;
  totalAmount: number;
  onlineInvoiceUrl: string;
  xeroUrl: string;
}

export async function generateInvoice(
  taxReturnId: string,
  totalAmount: number,
  reference?: string,
  xeroContactId?: string,
  forceCreateContact?: boolean,
  appointmentId?: string
): Promise<GenerateInvoiceResponse> {
  const response = await fetchApi<ApiResponse<GenerateInvoiceResponse>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/generate-invoice`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify({ totalAmount, reference, xeroContactId, forceCreateContact }),
    }
  );
  if (!response.data) throw new Error(response.error || 'Failed to generate invoice');
  return response.data;
}

export async function searchXeroContacts(name: string): Promise<XeroContact[]> {
  const response = await fetchApi<ApiResponse<XeroContact[]>>(
    `/xero/search-contacts?name=${encodeURIComponent(name)}`
  );
  return response.data || [];
}

export async function resolveXeroContactId(taxReturnId: string, appointmentId?: string, scheduleCBusinessId?: string): Promise<{ xeroContactId: string | null; resolvedFrom: string | null }> {
  let path = withAppointmentId(`/tax-returns/${taxReturnId}/resolve-xero-contact`, appointmentId);
  if (scheduleCBusinessId) {
    path += `${path.includes('?') ? '&' : '?'}scheduleCBusinessId=${encodeURIComponent(scheduleCBusinessId)}`;
  }
  const response = await fetchApi<ApiResponse<{ xeroContactId: string | null; resolvedFrom: string | null }>>(path);
  return response.data || { xeroContactId: null, resolvedFrom: null };
}

export async function voidInvoice(taxReturnId: string, appointmentId?: string): Promise<{ voidedInvoiceNumber?: string; voidedInvoiceId?: string }> {
  const response = await fetchApi<ApiResponse<{ voidedInvoiceNumber?: string; voidedInvoiceId?: string }>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/void-invoice`, appointmentId),
    { method: 'POST' }
  );
  return response.data || {};
}

export async function updateInvoiceAmount(taxReturnId: string, amount: number, appointmentId?: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/update-invoice-amount`, appointmentId),
    { method: 'POST', body: JSON.stringify({ amount }) }
  );
  return response.data!;
}

export async function refreshInvoiceFromXero(taxReturnId: string, appointmentId?: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/refresh-from-xero`, appointmentId),
    { method: 'POST' }
  );
  return response.data!;
}

export async function unlinkInvoice(taxReturnId: string, appointmentId?: string): Promise<{ taxReturnId?: string }> {
  const response = await fetchApi<ApiResponse<{ taxReturnId?: string }>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/unlink-invoice`, appointmentId),
    { method: 'POST' }
  );
  return response.data || {};
}

export interface LinkInvoiceResponse {
  success: boolean;
  invoiceId?: string;
  invoiceNumber?: string;
  status?: string;
  total?: number;
  amountDue?: number;
  amountPaid?: number;
  xeroUrl?: string;
  paymentUrl?: string;
  error?: string;
}

export async function linkXeroInvoice(taxReturnId: string, xeroInvoiceId: string): Promise<LinkInvoiceResponse> {
  const response = await fetchApi<ApiResponse<LinkInvoiceResponse>>(
    `/tax-returns/${taxReturnId}/link-invoice`,
    {
      method: 'POST',
      body: JSON.stringify({ xeroInvoiceId }),
    }
  );
  if (!response.data) throw new Error(response.error || 'Failed to link invoice');
  return response.data;
}

export interface RecordPaymentResponse {
  success: boolean;
  paymentId?: string;
  amount?: number;
  paymentType?: string;
  reference?: string;
  accountName?: string;
  invoiceStatus?: string;
  amountDue?: number;
  amountPaid?: number;
  error?: string;
}

export async function recordPayment(
  taxReturnId: string,
  amount: number,
  paymentType: 'check' | 'cash',
  reference?: string,
  appointmentId?: string
): Promise<RecordPaymentResponse> {
  const response = await fetchApi<ApiResponse<RecordPaymentResponse>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/record-payment`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify({ amount, paymentType, reference }),
    }
  );
  if (!response.data) throw new Error(response.error || 'Failed to record payment');
  return response.data;
}

// Cash Payment (no invoice)

export async function recordCashPayment(
  taxReturnId: string,
  amount: number,
  reference?: string,
  recordedBy?: string
): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(
    `/tax-returns/${taxReturnId}/record-cash-payment`,
    {
      method: 'POST',
      body: JSON.stringify({ amount, reference, recordedBy }),
    }
  );
  if (!response.data) throw new Error(response.error || 'Failed to record cash payment');
  return response.data;
}

export async function clearCashPayment(taxReturnId: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(
    `/tax-returns/${taxReturnId}/clear-cash-payment`,
    { method: 'POST' }
  );
  if (!response.data) throw new Error(response.error || 'Failed to clear cash payment');
  return response.data;
}

// Tax Documents API

export async function getTaxDocuments(taxReturnId: string): Promise<TaxDocument[]> {
  const response = await fetchApi<ApiResponse<TaxDocument[]>>(`/tax-returns/${taxReturnId}/documents`);
  return validateApiResponse(response.data || [], z.array(taxDocumentSchema), 'getTaxDocuments');
}

export interface UploadTaxDocumentResult {
  document: TaxDocument;
  message?: string; // 'PDF_PASSWORD_REQUIRED' | 'PDF_PASSWORD_INCORRECT' when EIN is needed/wrong
}

export async function uploadTaxDocument(
  taxReturnId: string,
  file: File,
  documentType: string,
  lastFourEin?: string,
  appointmentId?: string
): Promise<UploadTaxDocumentResult> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('documentType', documentType);
  if (lastFourEin) {
    formData.append('lastFourEin', lastFourEin);
  }

  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const url = withAppointmentId(`${API_BASE}/tax-returns/${taxReturnId}/documents`, appointmentId);
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    headers,
  });

  if (!response.ok) {
    throw new Error('Failed to upload document');
  }

  const result = await response.json() as ApiResponse<TaxDocument> & { message?: string };
  if (!result.data) throw new Error('Failed to upload document');
  return { document: result.data, message: result.message };
}

export async function deleteTaxDocument(taxReturnId: string, documentId: string): Promise<void> {
  await fetchApi<ApiResponse<void>>(`/tax-returns/${taxReturnId}/documents/${documentId}`, {
    method: 'DELETE',
  });
}

// Portal Uploads API (documents uploaded by clients via portal)

export interface PortalUpload extends TaxDocument {
  clientId?: string;
  clientName?: string;
  taxYear?: number;
}

export async function getPortalUploads(generalOnly = false): Promise<PortalUpload[]> {
  const url = generalOnly ? '/portal-uploads?generalOnly=true' : '/portal-uploads';
  const response = await fetchApi<ApiResponse<PortalUpload[]>>(url);
  return response.data || [];
}

export async function reassignPortalUpload(
  documentId: string,
  currentTaxReturnId: string,
  newTaxReturnId: string
): Promise<void> {
  await fetchApi<ApiResponse<unknown>>(`/portal-uploads/${documentId}/reassign`, {
    method: 'POST',
    body: JSON.stringify({ currentTaxReturnId, newTaxReturnId }),
  });
}

export async function downloadPortalUploadsZip(
  items: { id: string; taxReturnId: string }[]
): Promise<Blob> {
  const token = await getAccessToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(`${API_BASE}/portal-uploads/download-zip`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ items }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to download zip' }));
    throw new Error(error.error || `Download failed: ${response.status}`);
  }
  return response.blob();
}

// Semantic alias — the endpoint works for any TaxDocument, not just portal uploads.
// If we later rename the backend endpoint, only the underlying function changes.
export const downloadTaxDocumentsZip = downloadPortalUploadsZip;

// Tax Return Notes API

export async function addTaxReturnNote(
  taxReturnId: string,
  note: { content: string; createdBy?: string; createdByName?: string; isImportant?: boolean }
): Promise<{ id: string; content: string; createdAt: string; createdBy?: string; createdByName?: string; isImportant: boolean }> {
  const response = await fetchApi<ApiResponse<{ id: string; content: string; createdAt: string; createdBy?: string; createdByName?: string; isImportant: boolean }>>(`/tax-returns/${taxReturnId}/notes`, {
    method: 'POST',
    body: JSON.stringify(note),
  });
  if (!response.data) throw new Error('Failed to add note');
  return response.data;
}

export async function deleteTaxReturnNote(taxReturnId: string, noteId: string): Promise<void> {
  await fetchApi<ApiResponse<void>>(`/tax-returns/${taxReturnId}/notes/${noteId}`, {
    method: 'DELETE',
  });
}

// Dashboard API

export async function getDashboardStats(taxYear: number, preparerId?: string): Promise<DashboardStats> {
  const params = new URLSearchParams();
  params.append('taxYear', taxYear.toString());
  if (preparerId) params.append('preparerId', preparerId);
  const response = await fetchApi<ApiResponse<DashboardStats>>(`/dashboard/overview?${params.toString()}`);
  if (!response.data) throw new Error('Failed to get dashboard stats');
  return validateApiResponse(response.data, dashboardStatsSchema, 'getDashboardStats');
}

export async function getOutstandingItems(taxYear: number, preparerId?: string): Promise<OutstandingItems> {
  const params = new URLSearchParams();
  params.append('taxYear', taxYear.toString());
  if (preparerId) params.append('preparerId', preparerId);
  const response = await fetchApi<ApiResponse<OutstandingItems>>(`/dashboard/outstanding?${params.toString()}`);
  if (!response.data) throw new Error('Failed to get outstanding items');
  return response.data;
}

// Preparers API

export interface AvailablePreparer {
  id: string;
  name: string;
  email?: string;
}

export async function getPreparers(filters?: { taxYear?: number }): Promise<AvailablePreparer[]> {
  const params = new URLSearchParams();
  if (filters?.taxYear) params.append('taxYear', filters.taxYear.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<AvailablePreparer[]>>(`/preparers${query}`);
  return response.data || [];
}

// System Users API

export async function getSystemUsers(): Promise<SystemUser[]> {
  const response = await fetchApi<ApiResponse<SystemUser[]>>('/system-users');
  return response.data || [];
}

export async function getSystemUserByEmail(email: string): Promise<SystemUser | null> {
  try {
    const response = await fetchApi<ApiResponse<SystemUser>>(
      `/system-users/by-email?email=${encodeURIComponent(email)}`
    );
    return response.data || null;
  } catch {
    return null;
  }
}

// Upload Links API

export async function createUploadLink(data: {
  taxReturnId: string;
  requiredDocuments: string[];
  customMessage?: string;
}): Promise<DocumentUploadLink> {
  const response = await fetchApi<ApiResponse<DocumentUploadLink>>('/upload-links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!response.data) throw new Error('Failed to create upload link');
  return response.data;
}

export async function getUploadLinksForClient(clientId: string): Promise<DocumentUploadLink[]> {
  const response = await fetchApi<ApiResponse<DocumentUploadLink[]>>(`/upload-links?clientId=${clientId}`);
  return response.data || [];
}

// Appointment Import API

export interface ImportAppointmentsRequest {
  startDate?: string;
  endDate?: string;
  taxYear?: number;
}

export interface ImportResult {
  totalFound: number;
  imported: number;
  skipped: number;
  errors: string[];
}

export interface PendingAppointment {
  activityId: string;
  subject: string;
  scheduledStart: string;
  clientName: string;
  services?: string;
  appointmentType: string;
  alreadyImported: boolean;
}

export async function importAppointments(request?: ImportAppointmentsRequest): Promise<ImportResult> {
  const response = await fetchApi<ApiResponse<ImportResult>>('/appointments/import', {
    method: 'POST',
    body: JSON.stringify(request || {}),
  });
  if (!response.data) throw new Error('Failed to import appointments');
  return response.data;
}

export async function getPendingAppointments(startDate?: string, endDate?: string): Promise<PendingAppointment[]> {
  const params = new URLSearchParams();
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<PendingAppointment[]>>(`/appointments/pending${query}`);
  return response.data || [];
}

// Appointment Attendees API

export async function getAppointmentAttendees(appointmentId: string): Promise<AppointmentAttendeesResponse> {
  const response = await fetchApi<ApiResponse<AppointmentAttendeesResponse>>(`/appointments/${appointmentId}/attendees`);
  if (!response.data) throw new Error('Failed to get attendees');
  return response.data;
}

export async function resolveAttendees(appointmentId: string, request: ResolveAttendeesRequest): Promise<TaxReturn[]> {
  const response = await fetchApi<ApiResponse<TaxReturn[]>>(`/appointments/${appointmentId}/resolve`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  return response.data || [];
}

export async function addReturnToAppointment(appointmentId: string, request: AddReturnToAppointmentRequest): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(`/appointments/${appointmentId}/returns`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to add return');
  return response.data;
}

// Sync Client Info API

export interface SyncClientInfoResult {
  totalProcessed: number;
  updated: number;
  skipped: number;
  errors: string[];
}

export async function syncClientInfo(taxYear?: number): Promise<SyncClientInfoResult> {
  const response = await fetchApi<ApiResponse<SyncClientInfoResult>>('/tax-returns/sync-client-info', {
    method: 'POST',
    body: JSON.stringify({ taxYear }),
  });
  if (!response.data) throw new Error('Failed to sync client info');
  return response.data;
}

// Dataverse Search API

// Dataverse search responses can come back with mixed casing — PascalCase from the
// raw OData payload vs camelCase after our serializer normalizes it. Accept both.
type RawContact = Partial<Record<
  | 'contactId' | 'ContactId' | 'fullName' | 'FullName'
  | 'firstName' | 'FirstName' | 'lastName' | 'LastName'
  | 'email' | 'Email' | 'phone' | 'Phone'
  | 'mobilePhone' | 'MobilePhone'
  | 'parentAccountId' | 'ParentAccountId'
  | 'parentAccountName' | 'ParentAccountName',
  string
>>;
type RawAccount = Partial<Record<
  | 'accountId' | 'AccountId' | 'name' | 'Name'
  | 'email' | 'Email' | 'phone' | 'Phone'
  | 'primaryContactId' | 'PrimaryContactId'
  | 'primaryContactName' | 'PrimaryContactName',
  string
>>;
interface DataverseSearchResponse {
  contacts?: RawContact[];
  Contacts?: RawContact[];
  accounts?: RawAccount[];
  Accounts?: RawAccount[];
}

export async function searchDataverse(
  searchTerm: string,
  type: 'all' | 'contact' | 'account' = 'all',
  maxResults: number = DEFAULT_SEARCH_MAX_RESULTS
): Promise<SearchResults> {
  const params = new URLSearchParams();
  params.append('q', searchTerm);
  params.append('type', type);
  params.append('maxResults', maxResults.toString());
  const response = await fetchApi<ApiResponse<DataverseSearchResponse>>(`/dataverse/search?${params.toString()}`);
  const data = response.data;

  // Normalize property names (handle both PascalCase and camelCase from API)
  const rawContacts: RawContact[] = data?.contacts || data?.Contacts || [];
  const rawAccounts: RawAccount[] = data?.accounts || data?.Accounts || [];

  // Normalize individual contact objects
  const contacts: ContactInfo[] = rawContacts.map((c) => ({
    contactId: c.contactId || c.ContactId || '',
    fullName: c.fullName || c.FullName || '',
    firstName: c.firstName || c.FirstName,
    lastName: c.lastName || c.LastName,
    email: c.email || c.Email,
    phone: c.phone || c.Phone,
    mobilePhone: c.mobilePhone || c.MobilePhone,
    parentAccountId: c.parentAccountId || c.ParentAccountId,
    parentAccountName: c.parentAccountName || c.ParentAccountName,
  }));

  // Normalize individual account objects
  const accounts: AccountInfo[] = rawAccounts.map((a) => ({
    accountId: a.accountId || a.AccountId || '',
    name: a.name || a.Name || '',
    email: a.email || a.Email,
    phone: a.phone || a.Phone,
    primaryContactId: a.primaryContactId || a.PrimaryContactId,
    primaryContactName: a.primaryContactName || a.PrimaryContactName,
  }));

  return { contacts, accounts };
}

// Dataverse Create API

export interface CreateContactRequest {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}

export interface CreateAccountRequest {
  name: string;
  email?: string;
  phone?: string;
}

export async function createDataverseContact(request: CreateContactRequest): Promise<ContactInfo> {
  const response = await fetchApi<ApiResponse<ContactInfo>>('/dataverse/contacts', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to create contact');
  return response.data;
}

export async function createDataverseAccount(request: CreateAccountRequest): Promise<AccountInfo> {
  const response = await fetchApi<ApiResponse<AccountInfo>>('/dataverse/accounts', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to create account');
  return response.data;
}

// Outreach API

export interface EligibleClient {
  id: string;
  name: string;
  clientType: 'contact' | 'account';
  email: string | null;
  phone: string | null;
  preferredPreparerId: string | null;
  preferredPreparerName: string | null;
  lastFiledYear: number | null;
  alreadyContacted: boolean;
  isInHouse?: boolean;  // Optional - defaults to false if not provided
  inHouseServices?: string[];  // "bookkeeping", "payroll", "salestax"
  // Opt-out status
  isOptedOut?: boolean;
  optOutReason?: string;
  optOutChannel?: 'email' | 'sms' | 'both' | 'none';
  // Wave 2B: joined client-side from getPreparerByYear() -- NOT part of the eligible-clients
  // endpoint's own response. Undefined (not fetched yet) is a different fact from null (fetched,
  // this client has no computed last preparer); the filter column treats both as "no value".
  lastPreparerId?: string | null;
  lastPreparerName?: string | null;
}

/** Wave 2B: the stored per-client snapshot (each client's OWN most recent year). */
export interface PreparerHistorySnapshot {
  clientId: string;
  lastPreparerYear?: number | null;
  lastPreparerId?: string | null;
  lastPreparerName?: string | null;
}

export interface ClientOutreach {
  id: string;
  taxYear: number;
  clientId: string;
  clientName: string;
  clientEmail: string;
  campaignType: string;
  campaignId?: string;
  subject: string;
  emailBody?: string;  // HTML content of the sent email for preview
  sentAt: string;
  sentBy?: string;
  sentByName?: string;
  sentVia: string;
  emailOpened: boolean;
  emailOpenedAt?: string;
  linkClicked: boolean;
  linkClickedAt?: string;
}

export type EmailTemplateClientType = 'individual' | 'business' | 'auto' | 'all';

export interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  includesDocumentChecklist: boolean;
  includesQuestionnaire: boolean;
  clientType: EmailTemplateClientType;
}

export interface SendOutreachRequest {
  clientIds: string[];
  taxYear?: number;
  subject?: string;
  template?: string;
  campaignType?: string;
  campaignId?: string;
  clientType?: 'individual' | 'business'; // Optional override for auto-detection
  includeDocumentChecklist?: boolean;
  includeQuestionnaire?: boolean;
  taxLawUpdates?: string[];
  customBody?: string;
  sentBy?: string;
  sentByName?: string;
}

export interface SendOutreachResult {
  totalRequested: number;
  successCount: number;
  failCount: number;
  results: Array<{
    clientId: string;
    clientName?: string;
    clientType?: 'individual' | 'business';
    success: boolean;
    error?: string;
  }>;
}

// Wave 2B -- the same GET route serves two shapes depending on whether taxYear is passed (see
// api/Functions/PreparerByYearFunctions.cs): no year -> the stored snapshot (each client's own
// most recent year); a year -> every client live-evaluated against that ONE shared year. Exposed
// as two distinctly-typed functions rather than one function with a union return, so a caller
// can't accidentally read a TaxReturn field off a snapshot row or vice versa.

/** The default "last preparer" view -- each client's own most recent year. */
export async function getPreparerLastSnapshot(): Promise<PreparerHistorySnapshot[]> {
  const response = await fetchApi<ApiResponse<PreparerHistorySnapshot[]>>('/preparer-by-year');
  return response.data || [];
}

/** The year-picker's view -- every client evaluated against the SAME shared tax year. */
export async function getPreparerForYear(taxYear: number): Promise<TaxReturn[]> {
  const response = await fetchApi<ApiResponse<TaxReturn[]>>(`/preparer-by-year?taxYear=${taxYear}`);
  return response.data || [];
}

/** One preparer's roster for one tax year -- "Rogelio, 2026." */
export async function getPreparerByYearRoster(preparerId: string, taxYear: number): Promise<TaxReturn[]> {
  const response = await fetchApi<ApiResponse<TaxReturn[]>>(
    `/preparer-by-year/roster?preparerId=${encodeURIComponent(preparerId)}&taxYear=${taxYear}`
  );
  return response.data || [];
}

export async function getEligibleClients(yearsBack?: number): Promise<EligibleClient[]> {
  const params = new URLSearchParams();
  if (yearsBack) params.append('yearsBack', yearsBack.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<EligibleClient[]>>(`/outreach/eligible-clients${query}`);
  return response.data || [];
}

export async function getOutreachHistory(taxYear?: number, clientId?: string): Promise<ClientOutreach[]> {
  const params = new URLSearchParams();
  if (taxYear) params.append('taxYear', taxYear.toString());
  if (clientId) params.append('clientId', clientId);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<ClientOutreach[]>>(`/outreach/history${query}`);
  return response.data || [];
}

export async function getEmailTemplates(clientType?: 'individual' | 'business'): Promise<EmailTemplate[]> {
  const params = new URLSearchParams();
  if (clientType) params.append('clientType', clientType);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<EmailTemplate[]>>(`/outreach/templates${query}`);
  return response.data || [];
}

export async function sendOutreach(request: SendOutreachRequest): Promise<SendOutreachResult> {
  const response = await fetchApi<ApiResponse<SendOutreachResult>>('/outreach/send', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to send outreach');
  return response.data;
}

// Bookkeeping API

export async function getClientBookkeepingStatus(clientId: string): Promise<ClientBookkeepingStatus> {
  const response = await fetchApi<ApiResponse<ClientBookkeepingStatus>>(`/bookkeeping/client/${clientId}/status`);
  return response.data || {
    isBookkeepingClient: false,
    reviewsCompleted: 0,
    reviewsPending: 0,
    allReviews: []
  };
}

// Drake PDF Extraction API

// Custom error for duplicate checklist detection
export class DuplicateChecklistError extends Error {
  existingChecklist: DrakeChecklist;

  constructor(existingChecklist: DrakeChecklist, message: string) {
    super(message);
    this.name = 'DuplicateChecklistError';
    this.existingChecklist = existingChecklist;
  }
}

// Custom error for password-protected PDF
export class PasswordProtectedPdfException extends Error {
  pdfError: PasswordProtectedPdfError;

  constructor(pdfError: PasswordProtectedPdfError) {
    super(pdfError.message);
    this.name = 'PasswordProtectedPdfException';
    this.pdfError = pdfError;
  }
}

export async function uploadDrakePdf(
  file: File,
  taxYear: number,
  uploadedBy?: string,
  uploadedByName?: string,
  overwrite: boolean = false
): Promise<DrakeChecklist> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('taxYear', taxYear.toString());
  if (uploadedBy) formData.append('uploadedBy', uploadedBy);
  if (uploadedByName) formData.append('uploadedByName', uploadedByName);

  const url = `${API_BASE}/drake/upload${overwrite ? '?overwrite=true' : ''}`;
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
  });

  // Handle duplicate checklist (409 Conflict)
  if (response.status === 409) {
    const data = await response.json() as { existingChecklist: DrakeChecklist; message: string };
    throw new DuplicateChecklistError(data.existingChecklist, data.message || 'A checklist for this client already exists');
  }

  // Handle password-protected PDF (422 Unprocessable Entity)
  if (response.status === 422) {
    const data = await response.json() as PasswordProtectedPdfError;
    if (data.errorCode === 'PDF_PASSWORD_REQUIRED') {
      throw new PasswordProtectedPdfException(data);
    }
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `Upload failed: ${response.status}`);
  }

  const result = await response.json() as ApiResponse<DrakeChecklist>;
  if (!result.data) throw new Error('Failed to upload and extract PDF');
  return result.data;
}

export async function retryDrakeWithPassword(
  request: RetryWithPasswordRequest
): Promise<DrakeChecklist> {
  const response = await fetch(`${API_BASE}/drake/retry-with-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Retry failed' }));
    throw new Error(error.error || error.Error || `Retry failed: ${response.status}`);
  }

  const result = await response.json() as ApiResponse<DrakeChecklist>;
  if (!result.data) throw new Error('Failed to extract PDF with password');
  return result.data;
}

export async function getDrakeChecklists(
  taxYear?: number,
  matchStatus?: DrakeMatchStatus
): Promise<DrakeChecklist[]> {
  const params = new URLSearchParams();
  if (taxYear) params.append('taxYear', taxYear.toString());
  if (matchStatus) params.append('matchStatus', matchStatus);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<DrakeChecklist[]>>(`/drake/checklists${query}`);
  return response.data || [];
}

export async function getDrakeChecklist(id: string, taxYear: number): Promise<DrakeChecklist | null> {
  try {
    const response = await fetchApi<ApiResponse<DrakeChecklist>>(`/drake/checklists/${id}?taxYear=${taxYear}`);
    return response.data || null;
  } catch {
    return null;
  }
}

export async function getDrakeChecklistByClient(clientId: string, taxYear: number): Promise<DrakeChecklist | null> {
  try {
    const response = await fetchApi<ApiResponse<DrakeChecklist | null>>(`/drake/client/${clientId}/checklist?taxYear=${taxYear}`);
    return response.data || null;
  } catch {
    return null;
  }
}

export async function getDrakePendingReview(taxYear?: number): Promise<DrakeChecklist[]> {
  const query = taxYear ? `?taxYear=${taxYear}` : '';
  const response = await fetchApi<ApiResponse<DrakeChecklist[]>>(`/drake/pending-review${query}`);
  return response.data || [];
}

export async function matchDrakeChecklist(
  id: string,
  taxYear: number,
  clientId: string,
  matchedBy?: string,
  matchedByName?: string
): Promise<DrakeChecklist> {
  const response = await fetchApi<ApiResponse<DrakeChecklist>>(`/drake/checklists/${id}/match?taxYear=${taxYear}`, {
    method: 'POST',
    body: JSON.stringify({ clientId, matchedBy, matchedByName }),
  });
  if (!response.data) throw new Error('Failed to match checklist');
  return response.data;
}

export async function getDrakeMatchCandidates(id: string, taxYear: number): Promise<ContactInfo[]> {
  const response = await fetchApi<ApiResponse<ContactInfo[]>>(`/drake/checklists/${id}/candidates?taxYear=${taxYear}`);
  return response.data || [];
}

export async function updateDrakeChecklist(
  id: string,
  taxYear: number,
  updates: Partial<DrakeChecklist>
): Promise<DrakeChecklist> {
  const response = await fetchApi<ApiResponse<DrakeChecklist>>(`/drake/checklists/${id}?taxYear=${taxYear}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
  if (!response.data) throw new Error('Failed to update checklist');
  return response.data;
}

// ============ Drake Delete & Update Functions ============

export async function deleteDrakeChecklist(id: string, taxYear: number): Promise<void> {
  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  await fetch(`${API_BASE}/drake/checklists/${id}?taxYear=${taxYear}`, {
    method: 'DELETE',
    headers,
  });
}

export interface BulkDeleteResult {
  deleted: number;
  failed: number;
  errors: string[];
}

export async function bulkDeleteDrakeChecklists(ids: string[], taxYear: number): Promise<BulkDeleteResult> {
  const response = await fetchApi<ApiResponse<BulkDeleteResult>>('/drake/checklists/bulk-delete', {
    method: 'POST',
    body: JSON.stringify({ ids, taxYear }),
  });
  return response.data || { deleted: 0, failed: 0, errors: [] };
}

export async function updateDrakeItemReceived(
  checklistId: string,
  taxYear: number,
  itemIndex: number,
  received: boolean
): Promise<DrakeChecklist> {
  const response = await fetchApi<ApiResponse<DrakeChecklist>>(
    `/drake/checklists/${checklistId}/items/${itemIndex}/received?taxYear=${taxYear}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ received }),
    }
  );
  if (!response.data) throw new Error('Failed to update item received status');
  return response.data;
}

export async function deleteDrakeChecklistItem(
  checklistId: string,
  taxYear: number,
  itemIndex: number
): Promise<DrakeChecklist> {
  const response = await fetchApi<ApiResponse<DrakeChecklist>>(
    `/drake/checklists/${checklistId}/items/${itemIndex}?taxYear=${taxYear}`,
    {
      method: 'DELETE',
    }
  );
  if (!response.data) throw new Error('Failed to delete item');
  return response.data;
}

// ============ Drake Phase 4: Analytics & Learning ============

export async function recordDrakeCorrection(
  checklistId: string,
  taxYear: number,
  correction: RecordCorrectionRequest
): Promise<DrakeChecklist> {
  const response = await fetchApi<ApiResponse<DrakeChecklist>>(
    `/drake/checklists/${checklistId}/corrections?taxYear=${taxYear}`,
    {
      method: 'POST',
      body: JSON.stringify(correction),
    }
  );
  if (!response.data) throw new Error('Failed to record correction');
  return response.data;
}

export async function getDrakeAnalytics(
  taxYear?: number,
  startDate?: string,
  endDate?: string
): Promise<DrakeExtractionAnalytics> {
  const params = new URLSearchParams();
  if (taxYear) params.append('taxYear', taxYear.toString());
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<DrakeExtractionAnalytics>>(`/drake/analytics${query}`);
  return response.data || {
    totalExtractions: 0,
    totalCorrections: 0,
    correctionRate: 0,
    autoMatchRate: 0,
    averageConfidence: 0,
    averageProcessingTimeMs: 0,
    correctionsByField: [],
    correctionPatterns: [],
    promptVersionStats: [],
  };
}

export async function getDrakeCorrectionPatterns(taxYear?: number): Promise<CorrectionPattern[]> {
  const query = taxYear ? `?taxYear=${taxYear}` : '';
  const response = await fetchApi<ApiResponse<CorrectionPattern[]>>(`/drake/analytics/patterns${query}`);
  return response.data || [];
}

export async function getDrakePromptTemplates(): Promise<DrakePromptTemplate[]> {
  const response = await fetchApi<ApiResponse<DrakePromptTemplate[]>>('/drake/prompts');
  return response.data || [];
}

export async function getActiveDrakePrompt(): Promise<DrakePromptTemplate | null> {
  const response = await fetchApi<ApiResponse<DrakePromptTemplate | null>>('/drake/prompts/active');
  return response.data ?? null;
}

export async function saveDrakePromptTemplate(template: Partial<DrakePromptTemplate>): Promise<DrakePromptTemplate> {
  const response = await fetchApi<ApiResponse<DrakePromptTemplate>>('/drake/prompts', {
    method: 'POST',
    body: JSON.stringify(template),
  });
  if (!response.data) throw new Error('Failed to save prompt template');
  return response.data;
}

export async function activateDrakePromptTemplate(templateId: string): Promise<DrakePromptTemplate> {
  const response = await fetchApi<ApiResponse<DrakePromptTemplate>>(`/drake/prompts/${templateId}/activate`, {
    method: 'POST',
  });
  if (!response.data) throw new Error('Failed to activate prompt template');
  return response.data;
}

// ============ Drake Batch Upload ============

export interface DrakeUploadBatch {
  id: string;
  taxYear: number;
  uploadedAt: string;
  uploadedBy?: string;
  uploadedByName?: string;
  totalFiles: number;
  processed: number;
  matched: number;
  pendingReview: number;
  failed: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  files: BatchFileStatus[];
  completedAt?: string;
}

export interface BatchFileStatus {
  fileName: string;
  status: 'pending' | 'completed' | 'failed';
  checklistId?: string;
  error?: string;
  processedAt?: string;
}

export interface BatchUploadResponse {
  batchId: string;
  filesQueued: number;
  estimatedTimeMinutes: number;
  message: string;
}

export async function uploadDrakeBatch(
  files: File[],
  taxYear: number,
  uploadedBy?: string,
  uploadedByName?: string
): Promise<BatchUploadResponse> {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  const params = new URLSearchParams();
  params.append('taxYear', taxYear.toString());
  if (uploadedBy) params.append('uploadedBy', uploadedBy);
  if (uploadedByName) params.append('uploadedByName', uploadedByName);

  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/drake/upload-batch?${params.toString()}`, {
    method: 'POST',
    body: formData,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `Upload failed: ${response.status}`);
  }

  const result = await response.json() as ApiResponse<BatchUploadResponse>;
  if (!result.data) throw new Error('Failed to start batch upload');
  return result.data;
}

export async function getDrakeBatchStatus(batchId: string): Promise<DrakeUploadBatch> {
  const response = await fetchApi<ApiResponse<DrakeUploadBatch>>(`/drake/batches/${batchId}`);
  if (!response.data) throw new Error('Batch not found');
  return response.data;
}

// ============ Drake Async Job-Based Upload ============

export interface DrakeClientError {
  clientName: string;
  error: string;
  extractionTier?: string;
}

export interface DrakeExtractionJob {
  id: string;
  taxYear: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  blobName: string;
  originalFileName: string;
  totalClientsFound: number;
  checklistsCreated: number;
  checklistsMatched: number;
  createdChecklistIds: string[];
  error?: string;
  perClientErrors?: DrakeClientError[];
  createdAt: string;
  completedAt?: string;
  uploadedBy?: string;
  uploadedByName?: string;
}

export async function uploadDrakeAsync(
  file: File,
  taxYear: number,
  uploadedBy?: string,
  uploadedByName?: string
): Promise<DrakeExtractionJob> {
  const formData = new FormData();
  formData.append('file', file);

  const params = new URLSearchParams();
  params.append('taxYear', taxYear.toString());
  if (uploadedBy) params.append('uploadedBy', uploadedBy);
  if (uploadedByName) params.append('uploadedByName', uploadedByName);

  const token = await getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/drake/upload-async?${params.toString()}`, {
    method: 'POST',
    body: formData,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Upload failed' }));
    throw new Error(error.error || `Upload failed: ${response.status}`);
  }

  const result = await response.json() as ApiResponse<DrakeExtractionJob>;
  if (!result.data) throw new Error('Failed to start async upload');
  return result.data;
}

export async function getDrakeJobStatus(jobId: string): Promise<DrakeExtractionJob> {
  const response = await fetchApi<ApiResponse<DrakeExtractionJob>>(`/drake/jobs/${jobId}`);
  if (!response.data) throw new Error('Job not found');
  return response.data;
}

// ============ SMS Outreach API ============

import type {
  OutreachStats,
  SendSmsOutreachRequest,
  SmsOutreachResult,
} from '@/types';

export async function sendSmsOutreach(request: SendSmsOutreachRequest): Promise<SmsOutreachResult> {
  const response = await fetchApi<ApiResponse<SmsOutreachResult>>('/outreach/sms', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to send SMS outreach');
  return response.data;
}

export async function getOutreachStats(taxYear?: number): Promise<OutreachStats> {
  const params = new URLSearchParams();
  if (taxYear) params.append('taxYear', taxYear.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<OutreachStats>>(`/outreach/stats${query}`);
  return response.data || {
    totalContacted: 0,
    emailsSent: 0,
    smsSent: 0,
    openRate: 0,
    clickRate: 0,
    responseRate: 0,
    byDay: [],
    recentActivity: [],
  };
}

export async function getClientEngagement(taxYear?: number): Promise<Array<{
  clientId: string;
  clientName: string;
  email?: string;
  phone?: string;
  emailsReceived: number;
  smsReceived: number;
  emailsOpened: number;
  linksClicked: number;
  lastActivity?: string;
}>> {
  const params = new URLSearchParams();
  if (taxYear) params.append('taxYear', taxYear.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<Array<{
    clientId: string;
    clientName: string;
    email?: string;
    phone?: string;
    emailsReceived: number;
    smsReceived: number;
    emailsOpened: number;
    linksClicked: number;
    lastActivity?: string;
  }>>>(`/outreach/engagement${query}`);
  return response.data || [];
}

// ============ Notification API ============

import type {
  Notification,
  NotificationTypeOption,
  SendManualNotificationRequest,
  ResendNotificationRequest,
} from '@/types';

export async function getNotificationHistory(taxReturnId: string): Promise<Notification[]> {
  const response = await fetchApi<ApiResponse<Notification[]>>(`/tax-returns/${taxReturnId}/notifications`);
  return response.data || [];
}

export async function getNotificationTypes(): Promise<NotificationTypeOption[]> {
  const response = await fetchApi<ApiResponse<NotificationTypeOption[]>>('/notification-types');
  return response.data || [];
}

export async function sendManualNotification(request: SendManualNotificationRequest): Promise<Notification> {
  const response = await fetchApi<ApiResponse<Notification>>(`/tax-returns/${request.taxReturnId}/notifications/send`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to send notification');
  return response.data;
}

export async function resendNotification(notificationId: string, request: ResendNotificationRequest): Promise<Notification> {
  const response = await fetchApi<ApiResponse<Notification>>(`/notifications/resend/${notificationId}`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to resend notification');
  return response.data;
}

// ============ IRS Letter / Notice API ============

import type {
  IrsNotice,
  IrsNoticeDetail,
  IrsNoticeStats,
  IrsNoticeStatus,
  CreateIrsNoticeRequest,
  UpdateIrsNoticeRequest,
  LinkAmendmentRequest,
} from '@/types';

export interface IrsNoticeFilters {
  clientId?: string;
  status?: IrsNoticeStatus;
  assignedTo?: string;
  taxYear?: number;
  openOnly?: boolean;
}

/**
 * The staff queue. Passing clientId lets the server scope the Cosmos query to a single
 * partition instead of fanning out — always send it when you have it.
 */
export async function getIrsNotices(filters?: IrsNoticeFilters): Promise<IrsNotice[]> {
  const params = new URLSearchParams();
  if (filters?.clientId) params.append('clientId', filters.clientId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
  if (filters?.taxYear) params.append('taxYear', String(filters.taxYear));
  if (filters?.openOnly) params.append('openOnly', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<IrsNotice[]>>(`/irs-notices${query}`);
  return response.data || [];
}

export async function getIrsNoticeStats(): Promise<IrsNoticeStats> {
  const response = await fetchApi<ApiResponse<IrsNoticeStats>>('/irs-notices/stats');
  if (!response.data) throw new Error('Failed to load notice stats');
  return response.data;
}

/** clientId is REQUIRED — it is the partition key. The API 400s without it. */
export async function getIrsNotice(id: string, clientId: string): Promise<IrsNoticeDetail> {
  const response = await fetchApi<ApiResponse<IrsNoticeDetail>>(
    `/irs-notices/${id}?clientId=${encodeURIComponent(clientId)}`
  );
  if (!response.data) throw new Error('Notice not found');
  return response.data;
}

export async function getTaxReturnIrsNotices(taxReturnId: string, clientId?: string): Promise<IrsNotice[]> {
  const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
  const response = await fetchApi<ApiResponse<IrsNotice[]>>(`/tax-returns/${taxReturnId}/irs-notices${query}`);
  return response.data || [];
}

/**
 * Create a notice against a return.
 *
 * ⚠ Do NOT send clientId / taxYear / contactId — the server derives them from the return and
 * ignores anything the caller supplies. Writing the wrong guid does not error; it silently
 * orphans the notice from every record keyed on the real one.
 */
export async function createIrsNotice(
  taxReturnId: string,
  request: CreateIrsNoticeRequest
): Promise<IrsNotice> {
  const response = await fetchApi<ApiResponse<IrsNotice>>(`/tax-returns/${taxReturnId}/irs-notices`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to create notice');
  return response.data;
}

export async function updateIrsNotice(id: string, request: UpdateIrsNoticeRequest): Promise<IrsNotice> {
  const response = await fetchApi<ApiResponse<IrsNotice>>(`/irs-notices/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to update notice');
  return response.data;
}

export async function linkNoticeAmendment(id: string, request: LinkAmendmentRequest): Promise<IrsNotice> {
  const response = await fetchApi<ApiResponse<IrsNotice>>(`/irs-notices/${id}/amendments`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to link amendment');
  return response.data;
}

export async function unlinkNoticeAmendment(
  id: string,
  taxReturnId: string,
  clientId: string
): Promise<IrsNotice> {
  const response = await fetchApi<ApiResponse<IrsNotice>>(
    `/irs-notices/${id}/amendments/${taxReturnId}?clientId=${encodeURIComponent(clientId)}`,
    { method: 'DELETE' }
  );
  if (!response.data) throw new Error('Failed to unlink amendment');
  return response.data;
}

// ============ Client Inquiry API ============

import type {
  ClientInquiry,
  InquiryStats,
  InquiryStatus,
  UpdateInquiryRequest,
  ReplyToInquiryRequest,
} from '@/types';

export interface InquiryFilters {
  status?: InquiryStatus;
  clientId?: string;
  assignedTo?: string;
  priority?: string;
}

export async function getInquiries(filters?: InquiryFilters): Promise<ClientInquiry[]> {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.clientId) params.append('clientId', filters.clientId);
  if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
  if (filters?.priority) params.append('priority', filters.priority);
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<ClientInquiry[]>>(`/inquiries${query}`);
  return response.data || [];
}

export async function getInquiry(id: string, clientId: string): Promise<ClientInquiry> {
  const response = await fetchApi<ApiResponse<ClientInquiry>>(`/inquiries/${id}?clientId=${clientId}`);
  if (!response.data) throw new Error('Inquiry not found');
  return response.data;
}

export async function getInquiryStats(): Promise<InquiryStats> {
  const response = await fetchApi<ApiResponse<InquiryStats>>('/inquiries/stats');
  return response.data || {
    total: 0,
    new: 0,
    inProgress: 0,
    waitingForClient: 0,
    resolved: 0,
    unreadMessages: 0,
    byPriority: { urgent: 0, high: 0, normal: 0, low: 0 },
  };
}

export async function updateInquiry(id: string, request: UpdateInquiryRequest): Promise<ClientInquiry> {
  const response = await fetchApi<ApiResponse<ClientInquiry>>(`/inquiries/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to update inquiry');
  return response.data;
}

export async function replyToInquiry(id: string, request: ReplyToInquiryRequest): Promise<ClientInquiry> {
  const response = await fetchApi<ApiResponse<ClientInquiry>>(`/inquiries/${id}/reply`, {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to send reply');
  return response.data;
}

export async function markInquiryMessageRead(
  inquiryId: string,
  messageId: string,
  clientId: string
): Promise<void> {
  await fetchApi<ApiResponse<void>>(`/inquiries/${inquiryId}/messages/${messageId}/read`, {
    method: 'POST',
    body: JSON.stringify({ clientId }),
  });
}

// ============ Client Opt-Out API ============

import type {
  ClientOptOut,
  OptOutStats,
  CreateOptOutRequest,
  UpdateOptOutRequest,
} from '@/types';

export interface OptOutFilters {
  taxYear?: number;
  reason?: string;
  channel?: string;
  isActive?: boolean;
}

export async function getOptOuts(filters?: OptOutFilters): Promise<ClientOptOut[]> {
  const params = new URLSearchParams();
  if (filters?.taxYear) params.append('taxYear', filters.taxYear.toString());
  if (filters?.reason) params.append('reason', filters.reason);
  if (filters?.channel) params.append('channel', filters.channel);
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<ClientOptOut[]>>(`/opt-outs${query}`);
  return response.data || [];
}

export async function getOptOut(id: string): Promise<ClientOptOut> {
  const response = await fetchApi<ApiResponse<ClientOptOut>>(`/opt-outs/${id}`);
  if (!response.data) throw new Error('Opt-out record not found');
  return response.data;
}

export async function getOptOutByClientId(clientId: string, taxYear: number): Promise<ClientOptOut | null> {
  const response = await fetchApi<ApiResponse<ClientOptOut>>(`/opt-outs/client/${clientId}?taxYear=${taxYear}`);
  return response.data || null;
}

export async function getOptOutStats(taxYear?: number): Promise<OptOutStats> {
  const query = taxYear ? `?taxYear=${taxYear}` : '';
  const response = await fetchApi<ApiResponse<OptOutStats>>(`/opt-outs/stats${query}`);
  return response.data || {
    total: 0,
    byReason: {
      not_filing_this_year: 0,
      using_different_preparer: 0,
      doing_own_taxes: 0,
      deceased: 0,
      moved_away: 0,
      unsubscribe_request: 0,
      other: 0,
    },
    byChannel: { email: 0, sms: 0, both: 0, none: 0 },
    thisYear: 0,
    reactivated: 0,
  };
}

export async function createOptOut(request: CreateOptOutRequest): Promise<ClientOptOut> {
  const response = await fetchApi<ApiResponse<ClientOptOut>>('/opt-outs', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to create opt-out record');
  return response.data;
}

export async function updateOptOut(id: string, request: UpdateOptOutRequest): Promise<ClientOptOut> {
  const response = await fetchApi<ApiResponse<ClientOptOut>>(`/opt-outs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to update opt-out record');
  return response.data;
}

export async function reactivateClient(id: string, reactivatedBy?: string): Promise<ClientOptOut> {
  const response = await fetchApi<ApiResponse<ClientOptOut>>(`/opt-outs/${id}/reactivate`, {
    method: 'POST',
    body: JSON.stringify({ reactivatedBy }),
  });
  if (!response.data) throw new Error('Failed to reactivate client');
  return response.data;
}

export async function deleteOptOut(id: string): Promise<void> {
  await fetchApi<ApiResponse<void>>(`/opt-outs/${id}`, {
    method: 'DELETE',
  });
}

// ============ In-House Client Status API ============

import type { InHouseClientsResponse } from '@/types';

export async function getInHouseClientStatus(taxYear?: number): Promise<InHouseClientsResponse> {
  const params = new URLSearchParams();
  if (taxYear) params.append('taxYear', taxYear.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  const response = await fetchApi<ApiResponse<InHouseClientsResponse>>(`/inhouse-clients/status${query}`);
  if (!response.data) throw new Error('Failed to fetch in-house client status');
  return response.data;
}

// ============ Zoho Sign E-Signature API ============

import type {
  SignatureValidationResponse,
  SendForSignatureRequest,
  ZohoSignRequest,
  SignatureStatusResponse,
  Form8879Type,
  LinkedSignature,
  LinkSignatureRequest,
  RefreshLinkedSignatureResult,
  RequestCorrectionRequest,
  RequestCorrectionResponse,
} from '@/types';

export async function validateForSignature(
  taxReturnId: string,
  formType: Form8879Type,
  appointmentId?: string
): Promise<SignatureValidationResponse> {
  const response = await fetchApi<ApiResponse<SignatureValidationResponse>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/signature/validate`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify({ formType }),
    }
  );
  if (!response.data) throw new Error('Failed to validate for signature');
  const data = response.data;
  return {
    ...data,
    missingFields: data.missingFields ?? [],
    fieldDescriptions: data.fieldDescriptions ?? {},
  };
}

export async function sendForSignature(
  taxReturnId: string,
  request: SendForSignatureRequest,
  appointmentId?: string
): Promise<ZohoSignRequest> {
  const response = await fetchApi<ApiResponse<ZohoSignRequest>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/signature/send`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
  if (!response.data) throw new Error('Failed to send for signature');
  return response.data;
}

export async function getSignatureStatus(taxReturnId: string, appointmentId?: string): Promise<SignatureStatusResponse> {
  const response = await fetchApi<ApiResponse<SignatureStatusResponse>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/signature/status`, appointmentId)
  );
  if (!response.data) throw new Error('Failed to get signature status');
  return response.data;
}

export async function resendSignatureNotification(taxReturnId: string, appointmentId?: string): Promise<{ success: boolean; message: string }> {
  const response = await fetchApi<ApiResponse<{ success: boolean; message: string }>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/signature/resend`, appointmentId),
    {
      method: 'POST',
    }
  );
  if (!response.data) throw new Error('Failed to resend signature notification');
  return response.data;
}

export async function voidSignatureRequest(
  taxReturnId: string,
  reason: string,
  appointmentId?: string
): Promise<{ success: boolean; message: string }> {
  const response = await fetchApi<ApiResponse<{ success: boolean; message: string }>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/signature/void`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }
  );
  if (!response.data) throw new Error('Failed to void signature request');
  return response.data;
}

/**
 * Link an existing Zoho Sign envelope (initiated outside the app) to this tax return.
 * RequestId can be pasted as the raw ID or as a Zoho admin URL — backend extracts
 * the ID either way. Returns the newly-created LinkedSignature (with current status
 * from Zoho + signed-PDF blob name if the envelope was already signed at link time).
 */
export async function linkSignatureRequest(
  taxReturnId: string,
  request: LinkSignatureRequest,
  appointmentId?: string
): Promise<LinkedSignature> {
  const response = await fetchApi<ApiResponse<LinkedSignature>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/signature/link`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
  if (!response.data) throw new Error(response.error ?? 'Failed to link signature request');
  return response.data;
}

/**
 * Re-fetch a linked envelope's state from Zoho Sign and apply any drift to the local
 * record. Used when an envelope was signed externally but the webhook didn't make it
 * through (e.g. signed before the federal-8879 advance feature shipped, or webhook-secret
 * mismatch). Saves the signed PDF if not already saved, advances the federal return's
 * status if the linked envelope is a federal 8879 (8879/8879-C/8879-S/8879-PE).
 * Idempotent — re-running on an already-synced envelope is a Zoho round-trip + no-op.
 */
export async function refreshLinkedSignature(
  taxReturnId: string,
  requestId: string,
  appointmentId?: string,
): Promise<RefreshLinkedSignatureResult> {
  const response = await fetchApi<ApiResponse<RefreshLinkedSignatureResult>>(
    withAppointmentId(
      `/tax-returns/${taxReturnId}/signature/link/${encodeURIComponent(requestId)}/refresh`,
      appointmentId,
    ),
    { method: 'POST' },
  );
  if (!response.data) throw new Error(response.error ?? 'Failed to refresh linked signature');
  return response.data;
}

/**
 * Ask the client for a specific document to resolve a pending correction.
 * Creates a scoped upload link, sends an email with "how to obtain this document"
 * guidance (1095-A has the fullest treatment), and an SMS with a short summary +
 * upload link. Backend records a ClientOutreach entry so it shows up in history.
 */
export async function requestCorrectionDocuments(
  taxReturnId: string,
  request: RequestCorrectionRequest,
  appointmentId?: string,
): Promise<RequestCorrectionResponse> {
  const response = await fetchApi<ApiResponse<RequestCorrectionResponse>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/request-correction`, appointmentId),
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
  );
  if (!response.data) throw new Error(response.error ?? 'Failed to send correction request');
  return response.data;
}

// ============ Additional Invoices API ============

export async function addAdditionalInvoice(taxReturnId: string, totalAmount: number, description?: string, appointmentId?: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/additional-invoice`, appointmentId),
    { method: 'POST', body: JSON.stringify({ totalAmount, description }) }
  );
  if (!response.data) throw new Error('Failed to add additional invoice');
  return response.data;
}

export async function voidAdditionalInvoice(taxReturnId: string, invoiceId: string, appointmentId?: string): Promise<TaxReturn> {
  const response = await fetchApi<ApiResponse<TaxReturn>>(
    withAppointmentId(`/tax-returns/${taxReturnId}/additional-invoice/${invoiceId}/void`, appointmentId),
    { method: 'POST' }
  );
  if (!response.data) throw new Error('Failed to void additional invoice');
  return response.data;
}


// ============ Shared Invoices API ============

import type {
  ClientInvoice,
  CreateSharedInvoiceRequest,
  AllocatePaymentRequest,
} from '@/types';

export async function createSharedInvoice(request: CreateSharedInvoiceRequest): Promise<ClientInvoice> {
  const response = await fetchApi<ApiResponse<ClientInvoice>>('/invoices/shared', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to create shared invoice');
  return response.data;
}

export async function getSharedInvoices(taxYear: number, clientId?: string): Promise<ClientInvoice[]> {
  const params = new URLSearchParams();
  params.append('taxYear', taxYear.toString());
  if (clientId) params.append('clientId', clientId);
  const response = await fetchApi<ApiResponse<ClientInvoice[]>>(`/invoices/shared?${params.toString()}`);
  return response.data || [];
}

export async function getSharedInvoiceForTaxReturn(taxReturnId: string): Promise<ClientInvoice | null> {
  const response = await fetchApi<ApiResponse<ClientInvoice>>(`/tax-returns/${taxReturnId}/shared-invoice`);
  return response.data || null;
}

export async function allocatePayment(
  invoiceId: string,
  invoiceGroupId: string,
  request: AllocatePaymentRequest
): Promise<ClientInvoice> {
  const response = await fetchApi<ApiResponse<ClientInvoice>>(
    `/invoices/${invoiceId}/allocate-payment`,
    {
      method: 'POST',
      body: JSON.stringify({ ...request, invoiceGroupId }),
    }
  );
  if (!response.data) throw new Error('Failed to allocate payment');
  return response.data;
}

export async function voidSharedInvoice(
  invoiceId: string,
  invoiceGroupId?: string
): Promise<{ voidedInvoiceNumber?: string; clearedReturnCount?: number }> {
  const params = new URLSearchParams();
  if (invoiceGroupId) params.append('invoiceGroupId', invoiceGroupId);
  const response = await fetchApi<ApiResponse<{ voidedInvoiceNumber?: string; clearedReturnCount?: number }>>(
    `/invoices/${invoiceId}/void?${params.toString()}`,
    { method: 'POST' }
  );
  return response.data || {};
}

export async function recordSharedInvoicePayment(
  invoiceId: string,
  amount: number,
  paymentType?: 'check' | 'cash',
  reference?: string,
  invoiceGroupId?: string
): Promise<ClientInvoice> {
  const params = new URLSearchParams();
  if (invoiceGroupId) params.append('invoiceGroupId', invoiceGroupId);
  const response = await fetchApi<ApiResponse<ClientInvoice>>(
    `/invoices/${invoiceId}/record-payment?${params.toString()}`,
    {
      method: 'POST',
      body: JSON.stringify({ amount, paymentType, reference }),
    }
  );
  if (!response.data) throw new Error('Failed to record payment');
  return response.data;
}

export async function refreshInvoiceStatus(
  invoiceId: string,
  invoiceGroupId?: string
): Promise<ClientInvoice> {
  const params = new URLSearchParams();
  if (invoiceGroupId) params.append('invoiceGroupId', invoiceGroupId);
  const response = await fetchApi<ApiResponse<ClientInvoice>>(
    `/invoices/${invoiceId}/refresh?${params.toString()}`,
    { method: 'POST' }
  );
  if (!response.data) throw new Error('Failed to refresh invoice status');
  return response.data;
}

// ============ Xero Payment Sync API ============

export interface XeroPaymentSyncResult {
  checked: number;
  updatedToPaid: number;
  invoicesVoided?: number;
  autoCompleted: number;
  errors: number;
  sharedInvoicesChecked: number;
  sharedInvoicesUpdated: number;
  preparerBillsChecked: number;
  preparerBillsUpdated: number;
}

export async function syncXeroPayments(): Promise<XeroPaymentSyncResult> {
  const response = await fetchApi<ApiResponse<XeroPaymentSyncResult>>(
    '/invoices/sync-xero-payments',
    { method: 'POST' }
  );
  if (!response.data) throw new Error('Failed to sync Xero payments');
  return response.data;
}

// ============ Preparer Billing API ============

export async function getUnbilledReturns(taxYear: number, preparerId: string): Promise<TaxReturn[]> {
  const params = new URLSearchParams({ taxYear: taxYear.toString(), preparerId });
  const response = await fetchApi<ApiResponse<TaxReturn[]>>(`/preparer-bills/unbilled-returns?${params.toString()}`);
  if (!response.data) throw new Error('Failed to get unbilled returns');
  return response.data;
}

export async function getUnbilledReturnsSummary(taxYear: number): Promise<PreparerUnbilledSummary[]> {
  const params = new URLSearchParams({ taxYear: taxYear.toString() });
  const response = await fetchApi<ApiResponse<PreparerUnbilledSummary[]>>(`/preparer-bills/unbilled-summary?${params.toString()}`);
  return response.data || [];
}

export async function generatePreparerBill(request: GeneratePreparerBillRequest): Promise<PreparerBill> {
  const response = await fetchApi<ApiResponse<PreparerBill>>('/preparer-bills/generate', {
    method: 'POST',
    body: JSON.stringify(request),
  });
  if (!response.data) throw new Error('Failed to generate preparer bill');
  return response.data;
}

export async function getPreparerBills(preparerId?: string, status?: string): Promise<PreparerBill[]> {
  const params = new URLSearchParams();
  if (preparerId) params.append('preparerId', preparerId);
  if (status) params.append('status', status);
  const response = await fetchApi<ApiResponse<PreparerBill[]>>(`/preparer-bills?${params.toString()}`);
  if (!response.data) throw new Error('Failed to get preparer bills');
  return response.data;
}

export async function getPreparerBill(billId: string, preparerId: string): Promise<PreparerBill> {
  const params = new URLSearchParams({ preparerId });
  const response = await fetchApi<ApiResponse<PreparerBill>>(`/preparer-bills/${billId}?${params.toString()}`);
  if (!response.data) throw new Error('Failed to get preparer bill');
  return response.data;
}

export async function createXeroBillForPreparer(billId: string, preparerId: string): Promise<XeroBillResult> {
  const response = await fetchApi<ApiResponse<XeroBillResult>>(`/preparer-bills/${billId}/create-xero-bill`, {
    method: 'POST',
    body: JSON.stringify({ preparerId }),
  });
  if (!response.data) throw new Error('Failed to create Xero bill');
  return response.data;
}

export async function recordPreparerBillPayment(
  billId: string,
  request: RecordPaymentRequest
): Promise<PreparerBill> {
  const response = await fetchApi<ApiResponse<PreparerBill>>(
    `/preparer-bills/${billId}/record-payment`,
    {
      method: 'POST',
      body: JSON.stringify(request),
    }
  );
  if (!response.data) throw new Error('Failed to record payment');
  return response.data;
}

export async function updatePreparerBillLineItems(
  billId: string,
  preparerId: string,
  lineItems: Array<{ taxReturnId: string; amount: number }>
): Promise<PreparerBill> {
  const response = await fetchApi<ApiResponse<PreparerBill>>(
    `/preparer-bills/${billId}/line-items`,
    {
      method: 'PATCH',
      body: JSON.stringify({ preparerId, lineItems }),
    }
  );
  if (!response.data) throw new Error('Failed to update line items');
  return response.data;
}

// ============ REMINDER SETTINGS ============

export interface ReminderSetting {
  id: string;
  reminderType: string;
  intervalDays: number;
  maxAttempts: number;
  isEnabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

export async function getReminderSettings(): Promise<ReminderSetting[]> {
  const response = await fetchApi<ApiResponse<ReminderSetting[]>>('/reminder-settings');
  return response.data || [];
}

export async function updateReminderSetting(
  reminderType: string,
  updates: Partial<Pick<ReminderSetting, 'intervalDays' | 'maxAttempts' | 'isEnabled'>>
): Promise<ReminderSetting> {
  const response = await fetchApi<ApiResponse<ReminderSetting>>(
    `/reminder-settings/${reminderType}`,
    {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }
  );
  return response.data!;
}

// ============ USER PREFERENCES ============
//
// The "no default" ruling (fleet i18n standard, 2026-10-21): language is null until the person
// toggles it themselves; this API never assigns one. See LanguageToggle.tsx for how the value is
// applied exactly once per sign-in, so it never fights a live in-session choice.

export interface UserPreferences {
  id: string;
  userId: string;
  language: string | null;
  updatedAt?: string;
}

export async function getUserPreferences(): Promise<UserPreferences> {
  const response = await fetchApi<ApiResponse<UserPreferences>>('/user-preferences');
  return response.data!;
}

export async function updateLanguagePreference(language: 'en' | 'es'): Promise<UserPreferences> {
  const response = await fetchApi<ApiResponse<UserPreferences>>('/user-preferences', {
    method: 'PUT',
    body: JSON.stringify({ language }),
  });
  return response.data!;
}

// Staff Roles API

export async function getStaffRoles(): Promise<StaffRole[]> {
  const response = await fetchApi<ApiResponse<StaffRole[]>>('/staff-roles');
  return response.data || [];
}

export async function getStaffRole(systemUserId: string): Promise<StaffRole | null> {
  try {
    const response = await fetchApi<ApiResponse<StaffRole>>(`/staff-roles/${systemUserId}`);
    return response.data || null;
  } catch {
    return null;
  }
}

export async function upsertStaffRole(systemUserId: string, role: StaffRole): Promise<StaffRole> {
  const response = await fetchApi<ApiResponse<StaffRole>>(`/staff-roles/${systemUserId}`, {
    method: 'PUT',
    body: JSON.stringify(role),
  });
  return response.data!;
}

// Tax Clients API

export async function getTaxClients(): Promise<TaxClientListItem[]> {
  const response = await fetchApi<ApiResponse<TaxClientListItem[]>>('/tax-clients');
  return response.data || [];
}

export async function updateXeroContactId(
  clientId: string,
  xeroContactId: string | null
): Promise<void> {
  await fetchApi<ApiResponse<unknown>>(
    `/tax-clients/${encodeURIComponent(clientId)}/xero-contact-id`,
    {
      method: 'PATCH',
      body: JSON.stringify({ xeroContactId }),
    }
  );
}

// Filing history imports + match review

export async function uploadFilingHistoryImport(file: File, taxYear: number): Promise<FilingImportSummary> {
  const token = await getAccessToken();
  const fd = new FormData();
  fd.append('file', file);
  fd.append('taxYear', taxYear.toString());

  const res = await fetch(`${API_BASE}/imports/filing-history`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error ?? `Upload failed: ${res.status}`);
  }
  const body = await res.json() as ApiResponse<FilingImportSummary>;
  if (!body.data) throw new Error('Missing summary');
  return body.data;
}

export async function listFilingHistoryImports(): Promise<Array<{ importBatchId: string; taxYear: number; rowCount: number; pending: number; confirmed: number; auto: number }>> {
  const r = await fetchApi<ApiResponse<Array<{ importBatchId: string; taxYear: number; rowCount: number; pending: number; confirmed: number; auto: number }>>>(`/imports/filing-history`);
  return r.data ?? [];
}

export async function listMatchReviewRows(opts?: {
  taxYear?: number;
  take?: number;
  skip?: number;
}): Promise<MatchReviewPage> {
  const params = new URLSearchParams();
  if (opts?.taxYear) params.set('taxYear', String(opts.taxYear));
  if (opts?.take != null) params.set('take', String(opts.take));
  if (opts?.skip != null) params.set('skip', String(opts.skip));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const r = await fetchApi<ApiResponse<MatchReviewPage>>(`/match-review${qs}`);
  return r.data ?? { items: [], total: 0, nextSkip: null };
}

export async function bootstrapMatchReview(opts: {
  taxYear?: number;
  batchSize?: number;
  cursor?: number;
  commit?: boolean;
}): Promise<BootstrapPassResponse> {
  const params = new URLSearchParams();
  if (opts.taxYear != null) params.set('taxYear', String(opts.taxYear));
  if (opts.batchSize != null) params.set('batchSize', String(opts.batchSize));
  if (opts.cursor != null) params.set('cursor', String(opts.cursor));
  if (opts.commit === true) params.set('commit', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const r = await fetchApi<ApiResponse<BootstrapPassResponse>>(
    `/match-review/bootstrap${qs}`,
    { method: 'POST' }
  );
  return r.data ?? {
    processed: 0, autoMatched: 0, ambiguous: 0, noMatch: 0,
    proposals: [], skipped: [], nextCursor: null, total: 0
  };
}

export async function searchContacts(opts: {
  q: string;
  kind?: ContactSearchKind;
  take?: number;
}): Promise<ContactSearchResult[]> {
  const params = new URLSearchParams({ q: opts.q });
  if (opts.kind) params.set('kind', opts.kind);
  if (opts.take != null) params.set('take', String(opts.take));
  const r = await fetchApi<ApiResponse<ContactSearchResult[]>>(
    `/match-review/contacts/search?${params.toString()}`
  );
  return r.data ?? [];
}

export async function confirmMatchReview(id: string, contactId: string, taxYear: number): Promise<void> {
  await fetchApi<ApiResponse<unknown>>(`/match-review/${encodeURIComponent(id)}/confirm`, {
    method: 'POST',
    body: JSON.stringify({ contactId, taxYear }),
  });
}

export async function skipMatchReview(id: string, taxYear: number): Promise<void> {
  await fetchApi<ApiResponse<unknown>>(`/match-review/${encodeURIComponent(id)}/skip`, {
    method: 'POST',
    body: JSON.stringify({ taxYear }),
  });
}

// Priority outreach
export async function getPriorityOutreach(opts: {
  bucket?: string;
  returnType?: string;
  status?: string;
  take?: number;
  skip?: number;
}): Promise<PriorityListResponse> {
  const params = new URLSearchParams();
  if (opts.bucket) params.set('bucket', opts.bucket);
  if (opts.returnType) params.set('returnType', opts.returnType);
  if (opts.status) params.set('status', opts.status);
  if (opts.take != null) params.set('take', String(opts.take));
  if (opts.skip != null) params.set('skip', String(opts.skip));
  const r = await fetchApi<ApiResponse<PriorityListResponse>>(`/priority-outreach?${params}`);
  if (!r.data) throw new Error('Missing list');
  return r.data;
}

export async function setPriorityRowStatus(
  contactId: string,
  bucket: string,
  status: string,
  notes?: string
): Promise<void> {
  await fetchApi<ApiResponse<unknown>>(`/priority-outreach/${encodeURIComponent(contactId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ bucket, status, notes }),
  });
}

export async function resolveBucketContacts(
  bucket: string,
  channel: string,
  templateId: string,
  returnType?: string
): Promise<{ contactIds: string[] }> {
  const r = await fetchApi<ApiResponse<{ contactIds: string[] }>>(`/priority-outreach/campaign`, {
    method: 'POST',
    body: JSON.stringify({ bucket, channel, templateId, returnType }),
  });
  if (!r.data) throw new Error('Missing contactIds');
  return r.data;
}

// Export all as object for convenience
export const taxApi = {
  // Tax Returns
  getTaxReturns,
  getTaxReturn,
  createTaxReturn,
  updateTaxReturn,
  updateTaxReturnStatus,
  deleteTaxReturn,

  // Documents
  getTaxDocuments,
  uploadTaxDocument,
  deleteTaxDocument,

  // Dashboard
  getDashboardStats,
  getOutstandingItems,

  // Users
  getSystemUsers,
  getSystemUserByEmail,
  getPreparers,

  // Upload Links
  createUploadLink,
  getUploadLinksForClient,

  // Appointments
  importAppointments,
  getPendingAppointments,

  // Attendees
  getAppointmentAttendees,
  resolveAttendees,
  addReturnToAppointment,

  // Dataverse Search
  searchDataverse,

  // Sync
  syncClientInfo,

  // Outreach
  getEligibleClients,
  getOutreachHistory,
  getEmailTemplates,
  sendOutreach,
  sendSmsOutreach,
  getOutreachStats,
  getClientEngagement,

  // Bookkeeping
  getClientBookkeepingStatus,

  // Drake PDF Extraction
  uploadDrakePdf,
  getDrakeChecklists,
  getDrakeChecklist,
  getDrakeChecklistByClient,
  getDrakePendingReview,
  matchDrakeChecklist,
  getDrakeMatchCandidates,
  updateDrakeChecklist,

  // Drake Delete & Update
  deleteDrakeChecklist,
  bulkDeleteDrakeChecklists,
  updateDrakeItemReceived,
  deleteDrakeChecklistItem,

  // Drake Phase 4: Analytics & Learning
  recordDrakeCorrection,
  getDrakeAnalytics,
  getDrakeCorrectionPatterns,
  getDrakePromptTemplates,
  getActiveDrakePrompt,
  saveDrakePromptTemplate,
  activateDrakePromptTemplate,

  // Drake Batch Upload
  uploadDrakeBatch,
  getDrakeBatchStatus,

  // Drake Async Job Upload
  uploadDrakeAsync,
  getDrakeJobStatus,

  // Notifications
  getNotificationHistory,
  getNotificationTypes,
  sendManualNotification,
  resendNotification,

  // Client Inquiries
  getInquiries,
  getInquiry,
  getInquiryStats,
  updateInquiry,
  replyToInquiry,
  markInquiryMessageRead,

  // IRS Letters / Notices
  getIrsNotices,
  getIrsNoticeStats,
  getIrsNotice,
  getTaxReturnIrsNotices,
  createIrsNotice,
  updateIrsNotice,
  linkNoticeAmendment,
  unlinkNoticeAmendment,

  // Zoho Sign E-Signature
  validateForSignature,
  sendForSignature,
  getSignatureStatus,
  resendSignatureNotification,
  voidSignatureRequest,

  // Shared Invoices
  createSharedInvoice,
  getSharedInvoices,
  getSharedInvoiceForTaxReturn,
  allocatePayment,
  voidSharedInvoice,
  recordSharedInvoicePayment,

  // Xero Contact Search
  searchXeroContacts,

  // Xero Payment Sync
  syncXeroPayments,

  // Additional Invoices
  addAdditionalInvoice,
  voidAdditionalInvoice,

  // Preparer Billing
  getUnbilledReturns,
  getUnbilledReturnsSummary,
  generatePreparerBill,
  getPreparerBills,
  getPreparerBill,
  createXeroBillForPreparer,
  recordPreparerBillPayment,

  // Reminder Settings
  getReminderSettings,
  updateReminderSetting,

  // Staff Roles
  getStaffRoles,
  getStaffRole,
  upsertStaffRole,

  // Filing history imports + match review
  uploadFilingHistoryImport,
  listFilingHistoryImports,
  listMatchReviewRows,
  confirmMatchReview,
  skipMatchReview,

  // Priority outreach
  getPriorityOutreach,
  setPriorityRowStatus,
  resolveBucketContacts,
};

// ============ STAFF NOTIFICATIONS ============

/**
 * The calling staff member's own notification feed. Always scoped server-side to the caller —
 * there is deliberately no way to ask for someone else's queue.
 */
export async function getStaffNotifications(
  opts: { unreadOnly?: boolean; limit?: number } = {}
): Promise<StaffNotificationFeed> {
  const params = new URLSearchParams();
  if (opts.unreadOnly) params.set('unreadOnly', 'true');
  if (opts.limit) params.set('limit', String(opts.limit));
  const qs = params.toString();
  const response = await fetchApi<ApiResponse<StaffNotificationFeed>>(
    `/staff-notifications${qs ? `?${qs}` : ''}`
  );
  return response.data || { items: [], unreadCount: 0 };
}

/** Stamps `readAt` on one of the caller's own notifications. Idempotent. */
export async function markStaffNotificationRead(notificationId: string): Promise<void> {
  await fetchApi<ApiResponse<StaffNotification>>(
    `/staff-notifications/${notificationId}/read`,
    { method: 'POST' }
  );
}
